export const strings = {
  home: {
    welcome: "Welcome to Optymyse Steer.",
    healthChecking: "Checking system health…",
    healthOk: "System healthy.",
    healthError: "Could not reach the API.",
  },
  theme: {
    toggleLight: "Switch to light mode",
    toggleDark: "Switch to dark mode",
  },
  /** The states every screen draws besides its work (BUILD_SPEC §4.6). */
  states: {
    thisPage: "this page",
    unavailable: {
      title: "Couldn't load {what}",
      body: "Steer couldn't be reached just now. Nothing you've done is lost.",
      retry: "Try again",
    },
    notFound: {
      title: "{what} isn't here",
      body: "It may have been deleted or moved, or the link may be wrong.",
    },
    notAllowed: {
      title: "Only {who} can {what}",
      body: "Ask one of them if you need it.",
      home: "Back to the workspace",
    },
    noResults: {
      title: "Nothing matches these filters",
      clear: "Clear filters",
    },
    archived: {
      title: "This project is archived.",
      body: "Its work can be read and exported, and nothing in it can change.",
      bodyRestore: "Its work can be read and exported, and nothing in it can change until it is restored.",
      restore: "Restore project",
      restored: "Project restored",
      restoreFailed: "Couldn't restore the project",
    },
    offline: {
      title: "You're offline.",
      body: "Steer will catch up when you're back.",
      waiting: (n: number) =>
        n === 1
          ? "1 change is waiting to be saved, and is saved once you're back. Keep this page open until then."
          : `${n} changes are waiting to be saved, and are saved once you're back. Keep this page open until then.`,
    },
    who: {
      administrators: "Steer's Administrators",
      projectAdmins: "this project's admins and Steer's Administrators",
      contributors: "Administrators and Members",
    },
    /** What each page of Steer's settings lets its Administrators do. */
    adminPages: {
      settings: "change this workspace's settings",
      trackerImport: "import from Jira or Linear",
      github: "connect GitHub",
      slack: "connect Slack",
      audit: "read the audit trail",
      labels: "rename, merge and delete the workspace's labels",
      tokens: "see every API token in the workspace",
      usage: "see what the workspace uses",
      trash: "open the workspace's trash",
    },
  },
  auth: {
    banner: {
      unverified: "Verify your email to unlock all features.",
      resend: "Resend",
      resending: "Sending…",
      resentToast: "Verification email sent to {email}.",
    },
    header: {
      signOut: "Sign out",
      signingOut: "Signing out…",
    },
    signedOut: {
      unreachedTitle: "You're signed out of Steer",
      unreachedBody:
        "Optymyse couldn't be reached to sign you out everywhere, so its other apps may still be signed in. Sign out of one of them, or sign in here and try again.",
      signInAgain: "Sign in again",
    },
    regionUnavailable: {
      title: "Steer isn't available in your region yet",
      body: "Your Optymyse account is in a region where Steer isn't available yet. Steer has not signed you in, and has kept nothing about you.",
      back: "Back to Steer",
    },
  },
  workspaces: {
    none: {
      title: "No Steer workspace yet",
      body: "A Steer workspace belongs to an organisation at Optymyse. Ask one of its owners or admins to invite you to Steer. Once you have accepted the invitation from your email, sign in again.",
      signInAgain: "Sign in again",
      wrongAccount: "Not the right Optymyse account?",
    },
    switcher: {
      title: "Choose a workspace",
      lead: "You can use Steer in more than one workspace.",
      list: "Your workspaces",
      unchecked:
        "Optymyse couldn't be asked about some of your workspaces just now, so they aren't listed. Try again shortly.",
      notYou: "Not you?",
      /** The top bar's way to the central host's choice, where it has one to show. */
      all: "All your workspaces",
    },
    home: {
      welcome: "Welcome to {name}",
      tagline: "Projects and issues live here. (More coming in the next phase.)",
      settingsLink: "Workspace settings",
    },
    settings: {
      generalTitle: "General",
      nameLabel: "Workspace name",
      slugLabel: "URL slug",
      regionLabel: "Data location",
      regions: { eu: "European Union", us: "United States" },
      managedAtOptymyse:
        "This workspace is your organisation's at Optymyse. Its name and address, who owns and administers it, and closing it are managed there.",
    },
    /** A role in Steer, in the People page's words (SuiteUi's: an Administrator is its Admin). */
    roles: { administrator: "Admin", member: "Member", viewer: "Viewer" },
    people: {
      description:
        "{name}'s owners and admins choose who has Steer and who is its Admin. Steer's Administrators make people Members; everyone else is a Viewer.",
      member: "Can create and edit work, comment, upload and log time.",
      viewer: "Can read and export the projects they can see, and change nothing.",
    },
    access: {
      denied: {
        title: "No access to this workspace",
        body: "Your Optymyse account does not currently have access to this workspace in Steer. Ask a workspace owner or admin to give you access.",
      },
      unavailable: {
        title: "Workspace access can't be checked",
        body: "Steer cannot verify your access with Optymyse right now. Please try again shortly.",
      },
      unreachable: {
        title: "Steer can't be reached",
        body: "Steer couldn't load this workspace just now. Nothing you've done is lost. Please try again shortly.",
      },
      retry: "Try again",
      toWorkspaces: "Your workspaces",
    },
  },
  projects: {
    list: {
      title: "Projects",
      empty: "No projects yet",
      emptyBody: "Create your first project to start tracking issues.",
      createButton: "Create project",
      keyLabel: "Key",
      leadLabel: "Lead",
      private: "Private",
      join: "Join",
      joined: "You joined {name}",
      joinFailed: "Couldn't join the project. Try again.",
      joinHint: "A private project you aren't in. Joining it is recorded, and its members see you there.",
      thisProject: "this project",
      theProjects: "the workspace's projects",
      theArchived: "the archived projects",
      noneArchived: "No project is archived.",
      itsMembers: "its members",
      openPrivate: "open {name}",
      joinBody:
        "It's private. As an Administrator you may join it: joining is recorded, and its members see you there.",
      archived: "Archived projects",
      archivedBadge: "Archived",
      trash: "Trash",
    },
    create: {
      title: "Create a project",
      subtitle: "Projects hold your issues. Pick a name and a short key.",
      nameLabel: "Project name",
      namePlaceholder: "Marketing Website",
      keyLabel: "Key",
      keyHint: "Uppercase letters/digits, 2–10 chars. Used in issue keys like {key}-1.",
      descriptionLabel: "Description",
      descriptionPlaceholder: "What this project is about (optional)",
      submit: "Create project",
      submitting: "Creating…",
      cancel: "Cancel",
      successToast: "Created {name}",
      keyTakenError: "That key is taken in this workspace",
      templateLabel: "Start from",
    },
    /** What each template sets up (BUILD_SPEC §10.2). */
    templates: {
      kanban_software: {
        name: "Kanban software",
        description: "Epics, stories, tasks and bugs on a board: To do, In progress, In review, Done.",
      },
      scrum_software: { name: "Scrum software", description: "The same work, planned in sprints from a backlog." },
      bug_tracking: {
        name: "Bug tracking",
        description: "Reports arrive in Triage, then are Confirmed, Fixed, Won't fix or Duplicate.",
      },
      business: { name: "Business project", description: "Epics and tasks, planned by date." },
      client_delivery: {
        name: "Client delivery",
        description: "Work for a client, planned by date, through Client review to Delivered.",
      },
      blank: { name: "Blank", description: "Tasks and sub-tasks: To do, In progress, Done, Cancelled." },
    },
    home: {
      placeholderTitle: "Board coming soon",
      placeholderBody: "Issues, kanban board, and activity feed land in the next phase.",
      settingsLink: "Project settings",
    },
    settings: {
      title: "Project settings",
      manage: "change this project's settings",
      generalSection: "General",
      nameLabel: "Project name",
      keyLabel: "Key",
      keyHint: "Changing it keeps its old key finding the project, and its issues by their old keys.",
      descriptionLabel: "Description",
      iconLabel: "Icon",
      iconHint: "A letter or an emoji.",
      colorLabel: "Colour",
      noColor: "None",
      colors: {
        blue: "Blue",
        green: "Green",
        teal: "Teal",
        violet: "Violet",
        pink: "Pink",
        red: "Red",
        orange: "Orange",
        amber: "Amber",
        grey: "Grey",
      },
      leadSection: "Project lead",
      leadHint: "Its lead and its admins configure it. Steer's Administrators always can.",
      transferLeadLabel: "Transfer lead to",
      workSection: "Work",
      estimationLabel: "Estimate in",
      estimations: { points: "Story points", hours: "Hours", count: "Issue count", none: "No estimates" },
      defaultAssigneeLabel: "New issues are assigned to",
      defaultAssignees: { unassigned: "Nobody", lead: "The project's lead" },
      triageLabel: "New issues from outside the project arrive in Triage",
      save: "Save changes",
      saving: "Saving…",
      savedToast: "Project updated",
      saveFailed: "Couldn't save changes",
      accessSection: "Access",
      visibilityWorkspace: "Everyone with Steer",
      visibilityWorkspaceHint: "Everyone in the workspace sees it, and everyone who works in Steer works in it.",
      visibilityPrivate: "Its members only",
      visibilityPrivateHint:
        "Only the people named here see it. Steer's Administrators can see that it exists, and join it.",
      visibilityConfirmTitle: "Make {name} private?",
      visibilityConfirmBody:
        "Only its members will see it and its issues: its lead and you stay as its admins. Add anyone else who should keep seeing it.",
      visibilityConfirmAction: "Make it private",
      visibilitySaved: "Who sees {name} has changed",
      membersTitle: "Members",
      adminsTitle: "Admins",
      membersHint: "Its admins configure it; its members work in it; its viewers read it.",
      adminsHint: "Everyone with Steer works in it. Its admins configure it, as its lead does.",
      addMember: "Add",
      addMemberLabel: "Person to add",
      roleLabel: "Role for {name}",
      roles: { admin: "Admin", member: "Member", viewer: "Viewer" },
      lead: "Lead",
      removeMember: "Remove {name}",
      leave: "Leave",
      memberFailed: "Couldn't change who is in the project",
      workflowSection: "Workflow",
      workflowHint:
        "Each status belongs to a category, which says what it means: not started, started, done or cancelled. New work starts in the first To do status.",
      statusNameLabel: "Name of {name}",
      statusCategoryLabel: "Category of {name}",
      moveUp: "Move {name} up",
      moveDown: "Move {name} down",
      removeStatus: "Remove {name}",
      moving: "Moving its issues to {name}…",
      newStatusName: "New status",
      newStatusCategory: "Its category",
      addStatus: "Add status",
      removeStatusTitle: "Remove {name}?",
      removeStatusBody: "Its {count} issues move to the status you choose, each move in its issue's history.",
      removeStatusDestination: "Move its issues to",
      removeStatusAction: "Remove status",
      workflowFailed: "Couldn't change the workflow",
      trashSection: "Trash",
      trashHint: "Issues deleted in the last 30 days. After that they are deleted for good.",
      trashEmpty: "Nothing deleted lately.",
      restore: "Restore",
      restored: "Restored {key}",
      deletedAgo: "deleted {when}",
      archiveSection: "Archive",
      archiveBody: "An archived project leaves the active lists and planning. It stays readable, and can come back.",
      archiveAction: "Archive project",
      unarchiveBody: "This project is archived: readable, but nothing in it changes until it is restored.",
      unarchiveAction: "Restore project",
      archivedToast: "{name} archived",
      unarchivedToast: "{name} restored",
      dangerTitle: "Delete",
      deleteBody: "Moves the project and its issues to trash. Restore it within 30 days, or it is deleted for good.",
      deleteAction: "Delete project",
      deleteConfirmTitle: "Delete {name}?",
      deleteConfirmBody:
        "{name} and its {count} issues go to trash. Steer's Administrators can restore it within 30 days; then it is deleted for good.",
      deleteConfirmAction: "Delete project",
      deletedToast: "Project deleted",
      deleteFailed: "Couldn't delete the project",
    },
    trash: {
      title: "Trash",
      theTrash: "the trash",
      body: "Projects deleted in the last 30 days. After that they are deleted for good, with their issues.",
      empty: "No projects in trash.",
      restore: "Restore",
      restored: "Restored {name}",
      restoreFailed: "Couldn't restore the project",
      deletedAgo: "deleted {when}",
    },
  },
  labels: {
    title: "Labels",
    body: "The workspace's labels, shared by every project. A change to one is made on every issue carrying it, and recorded in its history.",
    empty: "No labels yet: they are made as people add them to issues.",
    nameLabel: "Name of {name}",
    colorLabel: "Colour of {name}",
    uses: "{count} issues",
    usesUnknown: "— issues",
    theLabels: "the workspace's labels",
    mergeLabel: "Merge {name} into another label",
    mergeInto: "Merge into…",
    merged: "{name} merged",
    delete: "Delete",
    deleteTitle: "Delete {name}?",
    deleteBody: "It comes off the {count} issues that carry it, each change in its history.",
    deleteBodyUncounted: "It comes off every issue that carries it, each change in its history.",
    failed: "Couldn't change the label",
  },
  /** A person's own work, across every project they can see (BUILD_SPEC §12.7). */
  myWork: {
    area: "My work",
    yourWork: "your work",
    views: {
      assigned: {
        title: "Assigned to me",
        body: "What's yours and unresolved, what's under way first, most urgent first.",
      },
      reported: { title: "Reported by me", body: "What you reported, latest changed first." },
      watching: { title: "Watching", body: "What you watch, latest changed first." },
      recent: { title: "Recently viewed", body: "The issues you looked at last." },
      due: { title: "Due soon", body: "Unresolved, due in the next 14 days or overdue, soonest first." },
    },
    empty: {
      assigned: "Nothing is assigned to you. Issues you're given land here.",
      reported: "You haven't reported anything yet.",
      watching: "You aren't watching anything. Issues you report, are assigned or comment on are watched for you.",
      recent: "Issues you open will be listed here.",
      due: "Nothing is due in the next 14 days.",
    },
    groupBy: "Group by",
    byCategory: "Status",
    byProject: "Project",
  },
  /** A list of issues (BUILD_SPEC §12.4). */
  list: {
    theList: "the list",
    columns: "Columns",
    chooseColumns: "Choose the columns",
    columnNames: {
      key: "Key",
      title: "Title",
      type: "Type",
      status: "Status",
      priority: "Priority",
      assignee: "Assignee",
      reporter: "Reporter",
      labels: "Labels",
      sprint: "Sprint",
      estimate: "Estimate",
      start: "Start",
      due: "Due",
      created: "Created",
      updated: "Updated",
      resolved: "Resolved",
      parent: "Parent",
    },
    groupBy: "Group by",
    groupNames: {
      none: "No grouping",
      status: "Status",
      category: "Status category",
      project: "Project",
      assignee: "Assignee",
      priority: "Priority",
      type: "Type",
      epic: "Epic",
      sprint: "Sprint",
      label: "Label",
    },
    noneGroup: { assignee: "Unassigned", epic: "No epic", sprint: "Backlog", label: "No label" },
    export: "Export CSV",
    sortBy: "Sort by {name}",
    selectAll: "Select every issue shown",
    selectRow: "Select {key}",
    selected: "{count} selected",
    clearSelection: "Clear selection",
    bulkLabel: "Change the selected issues",
    bulkStatus: "Set status",
    bulkAssignee: "Assign",
    bulkPriority: "Set priority",
    addLabel: "Add a label",
    removeLabel: "Remove a label",
    bulkSprint: "Set sprint",
    bulkDelete: "Delete",
    bulkDeleteTitle: "Delete {count}?",
    bulkDeleteBody: "They go to the project's trash, where they can be restored for 30 days.",
    bulkDone: "Changed {count}",
    bulkRefused: "{count} couldn't be changed: {reason}",
    bulkFailed: "Couldn't change them",
    statusOf: "Status of {key}",
    priorityOf: "Priority of {key}",
    assigneeOf: "Assignee of {key}",
    estimateOf: "Estimate of {key}",
    dueOf: "Due date of {key}",
    sprintOf: "Sprint of {key}",
    loadingMore: "Loading more…",
  },
  /** A project's board as its admins set it up (BUILD_SPEC §12.2). */
  boardSettings: {
    title: "Board",
    body: "The board's columns, in order, each holding one status or several, with limits on how many issues it should hold; its swimlanes; what its cards show.",
    columns: "Columns",
    columnName: "Name of the column holding {statuses}",
    namePlaceholder: "Its status's name",
    min: "Fewest",
    max: "Most",
    minOf: "Fewest issues in {name}",
    maxOf: "Most issues in {name}",
    moveUp: "Move {name} left",
    moveDown: "Move {name} right",
    removeColumn: "Take {name} off the board",
    removeStatus: "Take {name} out of this column",
    addStatus: "Add a status to {name}",
    addStatusOption: "Add a status…",
    newColumn: "Add a column for…",
    newColumnLabel: "Add a column for a status",
    offBoard: "Off the board",
    offBoardNone: "Every status is on the board.",
    offBoardBody: "Issues in these statuses aren't on the board; they're in the backlog and the list.",
    needsName: "A column holding several statuses needs a name",
    limitsWrong: "A column's fewest can't be more than its most",
    swimlanes: "Swimlanes, unless the person viewing chooses others",
    cardFields: "What cards show besides the key and title",
    fieldNames: {
      type: "Type",
      priority: "Priority",
      assignee: "Assignee",
      estimate: "Estimate",
      labels: "First two labels",
      due: "Due date",
      subtasks: "Sub-task progress",
      blocked: "Blocked",
    },
    doneDays: "Days resolved issues stay on the board",
    doneDaysHint: "In the Completed and Cancelled columns",
    reset: "Use the workflow's columns",
    save: "Save the board",
    saved: "Board saved",
    failed: "Couldn't save the board",
    unsaved: "Changes not saved yet",
  },
  /** A project's planning surface, its sprints and the issues waiting (BUILD_SPEC §12.3, §14.1). */
  backlog: {
    view: "Backlog",
    backlog: "Backlog",
    theBacklog: "the backlog",
    kanbanBody:
      "Issues waiting in the Backlog statuses, which the board leaves off. Move one to To do when its turn comes.",
    emptyBacklog: "Nothing is waiting. New issues land here until they're planned into a sprint.",
    emptySprint: "Plan this sprint: drag issues here, or use Move to… on an issue.",
    issue: ["issue", "issues"],
    point: ["point", "points"],
    hour: ["hour", "hours"],
    unestimated: "{count} without an estimate",
    capacity: "{planned} of {velocity}: the team's recent velocity",
    truncated: "The backlog holds {total} issues, more than it shows at once. Filter it to see the rest.",
    createIssue: "Create issue",
    newIssueTitle: "Title of the new issue",
    createFailed: "Couldn't create the issue",
    createSprint: "Create sprint",
    startSprint: "Start sprint",
    startBlocked: "{name} is still running: complete it first",
    completeSprint: "Complete sprint",
    editSprint: "Edit sprint",
    deleteSprint: "Delete sprint",
    sprintActions: "Actions for {name}",
    active: "Active",
    future: "Planned",
    daysLeft: "{days} left",
    day: ["day", "days"],
    lastDay: "Its last day",
    ended: "Its end date has passed",
    moveTo: "Move to…",
    moveOptions: "Move {key}",
    topOf: "Top of {name}",
    bottomOf: "Bottom of {name}",
    toTodo: "To do",
    moved: "Moved to {name}",
    refused: "{count} couldn't be moved: {reason}",
    moveFailed: "Couldn't move it. It's back where it was.",
    select: "Select {key}",
    selected: "{count} selected",
    clearSelection: "Clear selection",
    epics: "Epics",
    allIssues: "All issues",
    noEpic: "Issues without an epic",
    epicProgress: "{resolved} of {total} resolved",
    collapse: "Collapse {name}",
    expand: "Expand {name}",
    sprintName: "Name",
    sprintGoal: "Goal",
    startDate: "Start date",
    endDate: "End date",
    length: "Length",
    weeks: ["week", "weeks"],
    custom: "Custom",
    save: "Save",
    cancel: "Cancel",
    createTitle: "Create a sprint",
    editTitle: "Edit {name}",
    startTitle: "Start {name}",
    startBody:
      "It commits to {count} and {estimate} now. Issues added, removed or re-estimated later are recorded as changes to its scope.",
    start: "Start sprint",
    started: "{name} started",
    completeTitle: "Complete {name}",
    completeBody: "{completed} completed, {cancelled} cancelled, and {open} not resolved.",
    unresolvedTo: "Move its unresolved issues to",
    toBacklog: "The backlog",
    toNext: "{name}, the next sprint",
    toNew: "A new sprint",
    complete: "Complete sprint",
    completed: "{name} completed",
    deleteTitle: "Delete {name}?",
    deleteBody: "Its {count} go back to the backlog. A sprint that hasn't started has no history to keep.",
    delete: "Delete sprint",
    failed: "Couldn't change the sprint",
    datesInvalid: "The end date must be on or after the start date",
    noSprintTitle: "No sprint is running",
    noSprintBody: "A Scrum board shows the running sprint. Plan one in the backlog and start it there.",
    openBacklog: "Open the backlog",
    sprintField: "Sprint",
    noSprint: "Backlog",
    chooseSprint: "Sprint of {key}",
    history: "Sprints it has been in",
    outcome: { completed: "completed", cancelled: "cancelled", carried: "carried over", removed: "removed" },
  },
  issues: {
    /** The six status categories every status belongs to (BUILD_SPEC §11.1). */
    category: {
      triage: "Triage",
      backlog: "Backlog",
      unstarted: "To do",
      started: "In progress",
      completed: "Done",
      cancelled: "Cancelled",
    },
    /** The categories by the names the workflow editor gives them (BUILD_SPEC §11.1), apart from any status's. */
    categoryNames: {
      triage: "Triage",
      backlog: "Backlog",
      unstarted: "Unstarted",
      started: "Started",
      completed: "Completed",
      cancelled: "Cancelled",
    },
    type: { epic: "Epic", story: "Story", task: "Task", bug: "Bug", subtask: "Sub-task" },
    priority: { urgent: "Urgent", high: "High", medium: "Medium", low: "Low", none: "No priority" },
    board: {
      newIssue: "New issue",
      theBoard: "the board",
      emptyTitle: "Press C to create your first issue",
      emptyBody: "Issues you create land here. Drag between columns to change status.",
      emptyCreate: "Create issue",
      emptyInvite: "Invite teammates",
      countLabel: "{count} issues",
      showRest: "Show the other {count}",
      loadingRest: "Showing the rest…",
      moveFailed: "Couldn't move the issue. It's back where it was.",
      retry: "Retry",
      overMax: "Over its limit of {max}",
      underMin: "Under its minimum of {min}",
      limits: "Limits: {limits}",
      quickFilters: "Quick filters",
      savedFilter: "A saved filter",
      anySavedFilter: "Saved filters…",
      onlyMine: "Only my issues",
      recent: "Recently updated",
      blocked: "Blocked",
      lanes: "Swimlanes",
      laneNames: {
        none: "No swimlanes",
        assignee: "By assignee",
        epic: "By epic",
        priority: "By priority",
        type: "By type",
      },
      unassigned: "Unassigned",
      noEpic: "No epic",
      collapseLane: "Collapse {name}",
      expandLane: "Expand {name}",
      envelope:
        "This board holds {count} unresolved issues, more than it can show at once. Filter it to work on part of it.",
      moveTo: "Move to…",
      moveMenu: "Move {key}",
      topOf: "Top of {name}",
      bottomOf: "Bottom of {name}",
      overdue: "Overdue",
      due: "Due {date}",
      subtasks: "{done} of {total} sub-tasks resolved",
      settings: "Board settings",
      instructions:
        "Press Space to pick the card up, the arrow keys to move it, Space to drop it and Escape to put it back. Press M for Move to…, and Enter to open it.",
      pickedUp: "Picked up {key}. Arrow keys move it, Space drops it, Escape puts it back.",
      over: "{key} is at {place}.",
      dropped: "{key} dropped at {place}.",
      cancelled: "{key} put back where it was.",
      column: "column {name}",
      position: "position {position} of {count} in {name}",
    },
    list: {
      views: "Views of the project",
      viewBoard: "Board",
      viewList: "List",
      colKey: "Key",
      colTitle: "Title",
      colStatus: "Status",
      colPriority: "Priority",
      colAssignee: "Assignee",
      colUpdated: "Updated",
      loadMore: "Load more",
    },
    create: {
      title: "Create issue",
      titleLabel: "Title",
      titlePlaceholder: "Something needs doing…",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Add more detail (markdown supported)",
      typeLabel: "Type",
      priorityLabel: "Priority",
      assigneeLabel: "Assignee",
      unassigned: "Unassigned",
      submit: "Create issue",
      submitting: "Creating…",
      cancel: "Cancel",
      successToast: "Created {key}",
      errorToast: "Couldn't create the issue",
    },
    customer: {
      title: "Customer",
      company: "Company",
      contact: "Contact",
      none: "None",
      gone: "No longer in your directory",
      unavailable: "Your directory can't be read right now",
      choose: "Choose from your directory",
      findLabel: "Find a company or contact",
      searching: "Searching…",
      noResults: "Nothing in your directory matches",
      results: "Companies and contacts found",
      cancel: "Cancel",
      removeCompany: "Remove the company",
      removeContact: "Remove the contact",
    },
    drawer: {
      thisIssue: "this issue",
      changeNotSaved: "Couldn't save the change",
      open: "Open issue",
      expand: "Open full page",
      close: "Close",
      titleLabel: "Title",
      reporter: "Reporter",
      assignee: "Assignee",
      status: "Status",
      type: "Type",
      priority: "Priority",
      parent: "Parent",
      noParent: "None",
      descriptionEmpty: "No description yet.",
      edit: "Edit",
      save: "Save",
      saveHint: "⌘↵ to save · Esc to cancel",
      delete: "Delete issue",
      deleteUndo: "Issue deleted",
      deleteUndoAction: "Undo",
      deleteFailed: "Couldn't delete the issue",
      restoreFailed: "Couldn't bring the issue back",
      deleteConfirmTitle: "Delete this issue?",
      deleteConfirmBody: "This deletes {key}. You can undo it for ten seconds afterwards.",
      deleteConfirmAction: "Delete issue",
      backToBoard: "Back to board",
      label: "Issue",
      actions: "Actions for {key}",
      copyLink: "Copy link",
      copyKey: "Copy key",
      linkCopied: "Link to {key} copied",
      keyCopied: "{key} copied",
      copyFailed: "Couldn't copy it",
      details: "Details",
      created: "Created",
      updated: "Updated",
      resolved: "Resolved",
      breadcrumb: "Where it is",
      noLongerExists: "This issue no longer exists.",
      clone: "Clone…",
      move: "Move to another project…",
    },
    /** An issue cloned, choosing what to copy (BUILD_SPEC §12.1). */
    clone: {
      title: "Clone {key}",
      body: "The copy has its fields, starts in the first status in no sprint, and is linked as a clone. Comments are never copied.",
      subtasks: "Its sub-tasks",
      links: "Its links to other issues",
      attachments: "Its files",
      labels: "Its labels",
      submit: "Clone",
      done: "Cloned as {key}",
      filesFailed: "{count} of its files couldn't be copied",
      failed: "Couldn't clone it",
    },
    /** An issue moved to another project (BUILD_SPEC §10.3). */
    move: {
      title: "Move {key} to another project",
      project: "Project",
      chooseProject: "Choose a project",
      status: "Status there",
      type: "Type there",
      becomes: "It becomes {key}; {old} keeps finding it.",
      subtasks: "{count} move with it.",
      staying: "{count} stay here, and lose it as their epic.",
      sprint: "It leaves {name}.",
      parent: "It leaves its parent, {key}, which stays here.",
      watchers: "{count} who can't see that project stop watching it.",
      audited: "A private project is involved, so the move is in the audit trail.",
      noType: "That project has no type at this issue's level.",
      submit: "Move",
      done: "Moved to {key}",
      failed: "Couldn't move it",
    },
    filters: {
      status: "Status",
      assignee: "Assignee",
      type: "Type",
      search: "Search issues…",
      anyStatus: "Any status",
      anyType: "Any type",
      anyAssignee: "Anyone",
      priority: "Priority",
      anyPriority: "Any priority",
      me: "Assigned to me",
      unassigned: "Unassigned",
      clear: "Clear filters",
      emptyTitle: "No issues match these filters",
      emptyBody: "Try removing a filter to see more.",
    },
    fields: {
      title: "Details",
      labels: "Labels",
      none: "None",
      addLabel: "Add a label",
      findLabel: "Find or make a label",
      labelChoices: "Labels to choose",
      createLabel: "Make the label “{name}”",
      removeLabel: "Remove the label {name}",
      labelFailed: "Couldn't make the label",
      dates: "Dates",
      start: "Start date",
      due: "Due date",
      estimate: "Estimate",
      estimatePoints: "Estimate in story points",
      estimateHours: "Estimate in hours",
      points: "points",
      hours: "hours",
      parent: "Parent",
      setParent: "Set",
      changeParent: "Change",
      removeParent: "Remove",
      findParent: "Find the parent by key or title",
      parentChoices: "Issues it can belong to",
      subtasks: "Sub-tasks",
      epicChildren: "In this epic",
      addSubtask: "Add a sub-task",
      addChild: "Add an issue to this epic",
      newChildTitle: "Title of the new issue",
      childFailed: "Couldn't add it",
    },
    files: {
      title: "Files",
      empty: "No files yet.",
      add: "Add files",
      drop: "Drop files here to add them",
      adding: "Adding {name}…",
      tooLarge: "{name} is larger than 25 MB.",
      refusedType: "Steer doesn't take {name}: only pictures, PDFs, text, office documents, CSV and ZIP archives.",
      uploadFailed: "Couldn't add {name}",
      deleteLabel: "Delete {name}",
      deletedNote: "{name}, deleted",
      restore: "Bring it back",
      deleteFailed: "Couldn't delete the file",
      restoreFailed: "Couldn't bring the file back",
      by: "{name} · {when}",
    },
    links: {
      title: "Links",
      empty: "No links yet.",
      add: "Link an issue",
      addWeb: "Add a web link",
      relation: "Relation",
      search: "Find an issue by key or title",
      noResults: "No issue matches",
      cancel: "Cancel",
      url: "Address",
      webTitle: "Title, if you like",
      save: "Add",
      remove: "Remove the link to {what}",
      restricted: "Restricted issue",
      deleted: "Deleted issue",
      blocked: "Blocked",
      blockedHint: "An unresolved issue blocks this one",
      linkFailed: "Couldn't add the link",
      unlinkFailed: "Couldn't remove the link",
      web: "On the web",
      relations: {
        "blocks:outward": "blocks",
        "blocks:inward": "is blocked by",
        "relates:outward": "relates to",
        "relates:inward": "relates to",
        "duplicates:outward": "duplicates",
        "duplicates:inward": "is duplicated by",
        "clones:outward": "clones",
        "clones:inward": "is cloned by",
      },
    },
    comments: {
      title: "Comments",
      empty: "No comments yet.",
      placeholder: "Write a comment… Type @ to mention someone.",
      label: "New comment",
      editLabel: "Edit comment",
      send: "Comment",
      sendHint: "⌘↵ to send",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      delete: "Delete",
      edited: "edited",
      deletedNote: "Comment deleted",
      restore: "Bring it back",
      loadMore: "Show more comments",
      unseenOne: "Someone you mentioned can't see this issue, so wasn't told.",
      unseenMany: "{count} people you mentioned can't see this issue, so weren't told.",
      sendFailed: "Couldn't send the comment",
      editConflict: "This comment changed elsewhere, so your edit wasn't saved. It's showing the latest.",
      editFailed: "Couldn't save the comment",
      deleteFailed: "Couldn't delete the comment",
      restoreFailed: "Couldn't bring the comment back",
      mentionLabel: "People to mention",
      mentionNone: "No one matches",
      watch: "Watch",
      watching: "Watching",
      watchers: "{count} watching",
      watchFailed: "Couldn't change whether you watch this",
    },
    /** An edit refused because someone else changed the same field meanwhile (BUILD_SPEC §12.1, §23.4). */
    conflict: {
      title: "Someone else changed this meanwhile",
      body: "{fields} changed while you were editing {key}. Which do you want to keep?",
      field: "Field",
      theirs: "Now",
      yours: "Yours",
      keepTheirs: "Keep what it is now",
      useMine: "Use mine",
      none: "None",
      someIssue: "Another issue",
    },
    activity: {
      title: "Activity",
      empty: "No activity yet",
      show: "Show in the activity",
      showAll: "All",
      showComments: "Comments",
      showHistory: "History",
      created: "created this issue",
      deleted: "deleted this issue",
      titleChanged: "changed the title",
      descriptionChanged: "updated the description",
      statusChanged: "changed status from {from} to {to}",
      typeChanged: "changed type from {from} to {to}",
      priorityChanged: "changed priority from {from} to {to}",
      assigned: "assigned this to {name}",
      unassigned: "unassigned this",
      parentSet: "set parent to {key}",
      parentSetUnknown: "set a parent",
      parentRemoved: "removed the parent",
      customerSet: "set the customer",
      customerChanged: "changed the customer",
      customerRemoved: "removed the customer",
      restored: "restored this issue from trash",
      ranked: "moved this issue in the project's order",
      labelsChanged: "set the labels to {to}",
      labelsCleared: "removed the labels",
      linkAdded: "added a link: {relation} {key}",
      linkRemoved: "removed a link: {relation} {key}",
      someIssue: "an issue",
      webLinkAdded: "added a web link",
      webLinkRemoved: "removed a web link",
      startSet: "set the start date to {to}",
      startCleared: "cleared the start date",
      dueSet: "set the due date to {to}",
      dueCleared: "cleared the due date",
      estimateSet: "set the estimate to {to}",
      estimateCleared: "cleared the estimate",
      moved: "moved this from {from} to {to}",
      sprintSet: "moved this to {to}",
      sprintCleared: "moved this to the backlog",
      releaseSet: "put this in release {to}",
      releaseCleared: "took this out of its release",
      deletedUser: "Deleted user",
    },
  },
  /** The Inbox (BUILD_SPEC §12.8): what the person was told, by issue, newest first. */
  inbox: {
    area: "Inbox",
    title: "Inbox",
    yourInbox: "your Inbox",
    loading: "Loading your Inbox",
    notSaved: "Couldn't change your Inbox. It's as it was; try again.",
    section: "Notifications",
    body: "What you've been told about, newest first, each issue's together.",
    views: { all: "All", unread: "Unread", mentions: "Mentions", assignments: "Assignments" },
    viewsLabel: "Show",
    empty: {
      all: "Nothing here. When someone assigns, mentions or updates what you watch, you'll see it here.",
      unread: "You've read everything.",
      mentions: "No one has mentioned you.",
      assignments: "Nothing has been assigned to you.",
    },
    archiveRead: "Archive all read",
    archive: "Archive {what}",
    markRead: "Mark {what} read",
    markUnread: "Mark {what} unread",
    unread: "Unread",
    more: "and {count} more",
    unreadCount: "{count} unread",
    kinds: {
      assigned: "{actor} assigned it to you",
      unassigned: "{actor} unassigned you",
      mentioned: "{actor} mentioned you",
      commented: "{actor} commented",
      status: "{actor} moved it to {to}",
      resolved: "{actor} resolved it",
      due: "It's due tomorrow",
      overdue: "It was due on {due}",
      project_added: "{actor} added you to {project}",
      project_admin: "{actor} made you an admin of {project}",
      automation: "{rule}: {message}",
    },
    rulePaused: "The rule “{rule}” paused: {reason}",
    pausedReasons: {
      budget: "it did more than its hourly budget allows",
      owner_gone: "its owner's account is gone",
      owner_not_contributor: "its owner no longer changes work in Steer",
      owner_no_access: "its owner can no longer change this project's work",
      plan_feature: "the workspace's plan no longer includes the rule builder",
      plan_limit: "this month's automation actions are used up",
      plan_unavailable: "Steer couldn't confirm the workspace's plan",
    },
    kindNames: {
      assigned: "Assigned to you",
      unassigned: "Unassigned from you",
      mentioned: "Mentions of you",
      commented: "Comments on what you watch",
      status: "Status changes of what you watch",
      resolved: "What you watch, resolved",
      due: "Due tomorrow",
      overdue: "Overdue",
      project_added: "Added to a project",
      project_admin: "Made a project's admin",
      automation: "Messages from automation",
    },
  },
  /** A project's timeline, its roadmap (BUILD_SPEC §12.5, §14.3). */
  timeline: {
    view: "Timeline",
    theTimeline: "the timeline",
    body: "Its epics and their issues by date, what blocks what, and its releases as milestones.",
    zoomLabel: "How much the timeline shows at once",
    zooms: { weeks: "Weeks", months: "Months", quarters: "Quarters" },
    rows: "The timeline's epics and issues",
    noEpic: "No epic",
    today: "Today",
    expand: "Show the issues of {key}",
    collapse: "Hide the issues of {key}",
    bar: "{key} {title}, {from} to {to}",
    keys: "Arrows move it a day, Shift and an arrow a week; Alt and an arrow move its end. Enter opens it.",
    unscheduled: "No dates yet",
    empty: "No issues to show yet. Give an issue a start or due date and it joins the timeline.",
    more: "More issues than the timeline shows at once: the first 2,000 are here.",
    conflicts: "Dependencies that conflict",
    conflictCount: "Dependencies in conflict: {count}",
    conflict: "{blocker} ends after {dependent}, which it blocks, starts",
    shiftTitle: "Move what it blocks too? ({count})",
    shiftBody: "{key} would now end after these start. Move them by as much, or move only {key}.",
    shiftThem: "Move them too",
    onlyThis: "Move only this",
    moved: "{key}'s dates changed",
    movedSeveral: "Dates changed: {count}",
    refused: "Not changed, as someone else changed them meanwhile: {keys}",
    failed: "Couldn't change the dates",
  },
  /** A project's calendar (BUILD_SPEC §12.6): its issues by due date, or from start to due, by month or week. */
  calendar: {
    view: "Calendar",
    theCalendar: "the calendar",
    body: "Its issues on their due dates, or from their start to their due date. Drag one to another day to move it.",
    periodLabel: "How much the calendar shows at once",
    periods: { month: "Month", week: "Week" },
    modeLabel: "What the calendar shows",
    modes: { due: "Due dates", span: "Start to due" },
    previous: { month: "Previous month", week: "Previous week" },
    next: { month: "Next month", week: "Next week" },
    today: "Today, {day}",
    goToday: "Today",
    week: "Week of {day}",
    due: "{key} {title}, due {day}",
    day: "{key} {title}, {day}",
    span: "{key} {title}, {from} to {to}",
    release: "Release",
    released: "Released",
    more: "+{count} more",
    moreOn: " on {day}",
    fewer: "Show fewer",
    keys: "Arrows move it a day, up and down a week, Escape puts it back. Enter opens it.",
    keysRead: "Enter opens it.",
    empty: "Nothing on these days. Give an issue a due date and it joins the calendar.",
    tooMany: "More issues on these days than the calendar shows at once: the first 1,000 are here.",
    moved: "{key} moved to {day}",
    movedSpan: "{key} now runs from {from} to {to}",
    refused: "{key} wasn't moved, as someone else changed it meanwhile",
    failed: "Couldn't move {key}",
  },
  /** A project's releases (BUILD_SPEC §14.2): what is to ship, and when, and its notes. */
  releases: {
    view: "Releases",
    title: "Releases",
    theReleases: "the project's releases",
    thisRelease: "this release",
    body: "What's to ship, and when: each release's issues, how far they've come, and its notes.",
    field: "Release",
    chooseRelease: "Release of {key}",
    none: "None",
    empty: "No releases yet. A release gathers the issues to ship together, by a date.",
    create: "New release",
    createTitle: "New release",
    name: "Name",
    description: "Description",
    startDate: "Start",
    releaseDate: "Release date",
    save: "Save",
    cancel: "Cancel",
    created: "Release {name} made",
    states: { unreleased: "Unreleased", released: "Released", archived: "Archived" },
    progress: "{completed} of {total} completed",
    overdue: "Past its date with unresolved issues",
    issues: "Issues",
    noIssues: "No issues in this release yet. Choose it as an issue's release to add one.",
    blocked: "Blocked",
    notes: "Release notes",
    notesEmpty: "No notes yet.",
    draftNotes: "Draft from completed issues",
    editNotes: "Edit notes",
    saveNotes: "Save notes",
    notesSaved: "Notes saved",
    release: "Release",
    releaseTitle: "Release {name}",
    releaseBody: "Records today as its release date. Nothing is resolved; its unresolved issues go where you choose.",
    unresolvedTo: "Its {count} unresolved issues",
    stayHere: "Stay in this release",
    moveTo: "Move to {name}",
    released: "{name} released",
    unrelease: "Unrelease",
    archive: "Archive",
    restore: "Restore",
    delete: "Delete",
    deleteTitle: "Delete {name}?",
    deleteBody: "Its issues stay, in no release. This can't be undone.",
    deleted: "Release deleted",
    burnup: "Burnup",
    burnupSummary: "{completed} of {scope} {unit} completed so far, toward {date}.",
    burnupDefinition:
      "Each day's end: the issues the release held then, by their history, and of them those in a Completed status then. Cancelled work isn't completed.",
    completed: "Completed",
    scope: "Scope",
    backToReleases: "Releases",
    showArchived: "Show archived releases",
    failed: "Couldn't change the release",
  },
  /** A project's reports (BUILD_SPEC §16): what happened, from its record, each with how it is counted. */
  reports: {
    view: "Reports",
    title: "Reports",
    body: "What happened in the project, from its record: never just how its issues are now.",
    kinds: { sprint: "Sprint", velocity: "Velocity", flow: "Flow" },
    kindsLabel: "Report",
    noData: "No data",
    numbers: "Show the numbers",
    downloadCsv: "Download as CSV",
    when: "When",
    what: "What",
    count: "Count",
    definitions: "How it's counted",
    points: "points",
    hours: "hours",
    issues: "issues",
    sprint: {
      choose: "Sprint",
      theSprints: "the project's sprints",
      theBurndown: "the burndown",
      theReport: "the sprint's report",
      none: "No sprint has started yet. A sprint's reports begin when it starts.",
      burndown: "Burndown",
      burndownSummary: "{name}: remaining {unit} went from {from} to {to}; the ideal falls to nothing by {end}.",
      burnup: "Burnup",
      burnupSummary: "{name}: {completed} of {scope} {unit} completed.",
      remaining: "Remaining",
      ideal: "Ideal",
      completed: "Completed",
      scope: "Scope",
      changes: "Scope changed after the start",
      changeAdded: "{key} added (+{delta})",
      changeRemoved: "{key} removed ({delta})",
      changeEstimate: "{key} re-estimated ({delta})",
      aRemovedIssue: "An issue since deleted",
      report: "The sprint",
      lists: {
        completed: "Completed",
        notCompleted: "Not completed",
        cancelled: "Cancelled",
        removed: "Removed",
        added: "Added after the start",
      },
      emptyList: "None",
      definition:
        "Remaining is the estimate of the sprint's issues not yet resolved, at each day's end; scope is what it committed to at its start, plus what was added, less what was removed. Cancelled work is resolved, never completed. Read from the sprint's own record, so it stays what happened whatever its issues are now.",
    },
    flow: {
      theFlow: "the project's flow",
      range: "Over",
      rangeLabel: "The days the reports cover",
      ranges: { "30": "30 days", "90": "90 days" },
      interval: "By",
      intervalLabel: "How the counts are grouped",
      intervals: { day: "Day", week: "Week" },
      groupBy: "Show",
      groupByLabel: "What the cumulative flow shows",
      groups: { category: "Categories", status: "Statuses" },
      cumulative: "Cumulative flow",
      cumulativeSummary: "Issues by {by} at each day's end, from {from} to {to}: {now} now.",
      cumulativeDefinition:
        "How many of the project's issues were in each category, or status, at each day's end, as their flow had it then: a status the workflow recategorised counts in its new category from that day, never before.",
      categories: {
        triage: "Triage",
        backlog: "Backlog",
        unstarted: "Not started",
        started: "Started",
        completed: "Completed",
        cancelled: "Cancelled",
      },
      cycle: "Cycle time",
      cycleSummary: "Completed over these days: {count}, 85% of them within {p85} days.",
      cycleNone: "No issue was completed in these days.",
      cycleDefinition:
        "Each completed issue, by the days from its first entering a Started status to its completion. Half took less than the 50th percentile; 85% less than the 85th. An issue completed without being started, or cancelled, has no cycle time.",
      percentile: "{p}th percentile",
      days: "Days",
      lead: "Lead time, from creation: 50% within {p50} days, 85% within {p85} days.",
      created: "Created against resolved",
      createdSummary: "Created over these days: {created}; resolved: {resolved}.",
      createdLine: "Created",
      resolvedLine: "Resolved",
      createdDefinition:
        "Issues made in the project in each period, and those resolved in it, Completed or Cancelled. A period before the project's first issue has no data.",
      throughput: "Throughput",
      throughputSummary: "Completed over these days: {count}.",
      throughputBar: "Completed",
      throughputDefinition:
        "Issues completed in each period, each counted once however often it was resolved; cancelled work isn't throughput. One reopened and completed again counts again in the period it was.",
      reopened: "Reopened over these days: {count}.",
      workload: "Workload",
      workloadCaption: "Open issues per assignee now",
      workloadNone: "No open issues.",
      assignee: "Assignee",
      unassigned: "Unassigned",
      open: "Open",
      estimate: "Estimate",
      workloadDefinition:
        "Each person's open issues in the project now, and their estimate: the team's load, never a score of anyone.",
    },
    velocity: {
      title: "Velocity",
      theVelocity: "the velocity",
      none: "No sprint has been completed yet.",
      summary: "The last {count} sprints: on average {average} {unit} completed of {committed} committed.",
      committed: "Committed",
      completed: "Completed",
      definition:
        "For each of the last seven completed sprints: the estimate committed at its start, beside the estimate of the issues completed within it. Cancelled work counts in neither.",
    },
  },
  /** Live updates and presence (BUILD_SPEC §15.5). */
  live: {
    editingOne: "{names} is editing the description",
    editingMany: "{names} are editing the description",
  },
  /** A person's own preferences (BUILD_SPEC §15.4, §4.3): kept with them in this workspace. */
  preferences: {
    title: "Preferences",
    yourPreferences: "your preferences",
    body: "Yours alone, in this workspace, the same in every browser.",
    section: "Your Inbox, reminders and keyboard",
    inbox: "Your Inbox",
    inboxHint: "Mentions and assignments always reach you.",
    mutedProjects: "Quiet projects",
    mutedHint: "What you watch in these tells you nothing; mentions and assignments still do.",
    watching: "Watching",
    watchOnComment: "Watch issues I comment on",
    watchOnMention: "Watch issues I'm mentioned in",
    reminders: "Reminders",
    reminderTime: "When due and overdue reminders come",
    timezone: "Your time zone",
    keyboard: "Keyboard",
    email: "Email",
    emailHint: "Also emailed to you, of what reaches your Inbox. Each email's own link stops them all.",
    emailKind: "Email: {kind}",
    emailEvery: "How often",
    emailEveryNames: { instant: "As it happens", hourly: "An hourly digest", daily: "A daily digest" },
    quietHours: "Quiet hours: hold email until they end",
    quietFrom: "From",
    quietTo: "Until",
    saved: "Saved",
    failed: "Couldn't save your preferences",
  },
  /** Dashboards (BUILD_SPEC §16.5): a person's grid of gadgets, shared as a filter is, showing each viewer their own. */
  dashboards: {
    area: "Dashboards",
    thisDashboard: "this dashboard",
    theDashboards: "your dashboards",
    all: "All dashboards",
    allBody: "Every dashboard you can see: yours, and those shared with you.",
    yours: "Your dashboards",
    shared: "Shared with you",
    title: "Dashboards",
    body: "A dashboard gathers what you look at often: a filter's issues, a project's reports, your work, a note.",
    empty: "No dashboards yet. Make one to keep a filter's issues, a project's reports and your work in one place.",
    create: "New dashboard",
    createTitle: "New dashboard",
    editTitle: "Edit “{name}”",
    name: "Name",
    description: "Description",
    descriptionHint: "What it's for, if its name doesn't say.",
    sharing: "Who sees it",
    sharingPrivate: "Only you",
    sharingProject: "A project's people",
    sharingWorkspace: "Everyone in the workspace",
    sharingHint:
      "Sharing a dashboard shares its gadgets, never their data: each person sees only the issues and reports they may.",
    project: "Project",
    chooseProject: "Choose a project",
    save: "Save",
    cancel: "Cancel",
    created: "Made “{name}”",
    saved: "Saved",
    saveFailed: "Couldn't save the dashboard",
    conflict: "Someone else changed this dashboard just now: it shows their changes. Make yours again.",
    owner: "{name}'s",
    gadgetCount: "Gadgets: {count}",
    edit: "Edit",
    delete: "Delete",
    deleteTitle: "Delete “{name}”?",
    deleteBody: "Anyone it's shared with will no longer have it. What its gadgets show stays as it is.",
    deleted: "Deleted “{name}”",
    notFound: "This dashboard doesn't exist, or isn't shared with you.",
    noGadgets: "No gadgets yet.",
    noGadgetsEdit: "No gadgets yet. Add one to show a filter's issues, a project's reports, your work or a note.",
    addGadget: "Add a gadget",
    gadgetActions: "{name}: what to do with it",
    editGadget: "Edit",
    moveEarlier: "Move earlier",
    moveLater: "Move later",
    makeFull: "Make it full width",
    makeHalf: "Make it half width",
    removeGadget: "Remove",
    removed: "Removed “{name}”",
    full: "Dashboard full: it holds at most {count} gadgets.",
    kinds: {
      issues: { name: "Filter results", body: "A saved filter's issues, or a project's." },
      counts: {
        name: "Counts by field",
        body: "How many issues there are by status, assignee, priority or another field.",
      },
      table: { name: "Two-field table", body: "Issues counted by two fields at once, as rows and columns." },
      created: { name: "Created vs resolved", body: "Issues made against those resolved, in a project." },
      burndown: { name: "Sprint burndown", body: "What remains of a sprint, against its ideal." },
      velocity: { name: "Velocity", body: "What a project's last sprints committed to and completed." },
      flow: { name: "Cumulative flow", body: "A project's issues by category, day by day." },
      assigned: { name: "Assigned to me", body: "Your unresolved issues, most urgent first." },
      activity: {
        name: "Recent activity",
        body: "The latest changes and comments, in every project you can see or one.",
      },
      note: { name: "Note", body: "Words of your own, in Markdown." },
    },
    gadgetTitleAdd: "Add a gadget",
    gadgetTitleEdit: "Edit “{name}”",
    gadgetHint: "Each person sees in it only what they may.",
    kind: "Gadget",
    gadgetName: "Title",
    gadgetNameHint: "Leave it empty and it's named for what it shows.",
    width: "Width",
    widths: { half: "Half", full: "Full" },
    source: "Issues from",
    sources: { filter: "A saved filter", project: "A project" },
    filter: "Filter",
    chooseFilter: "Choose a filter",
    privateFilter: "Only you can see this filter: others will see nothing in this gadget.",
    by: "Count by",
    chart: "Shown as",
    charts: { bar: "Bars", pie: "Pie" },
    rows: "Rows",
    columns: "Columns",
    days: "Over",
    dayChoices: { 30: "The last 30 days", 90: "The last 90 days" },
    sprint: "Sprint",
    activeSprint: "The active sprint, whichever it is",
    limit: "Show",
    limitChoice: "{count} issues",
    activityLimitChoice: "{count} entries",
    activityScope: "From",
    allProjects: "Every project you can see",
    text: "Note",
    textHint: "Markdown: **bold**, _italic_, lists and links.",
    add: "Add",
    fields: {
      status: "Status",
      category: "Category",
      project: "Project",
      assignee: "Assignee",
      priority: "Priority",
      type: "Type",
      epic: "Epic",
      sprint: "Sprint",
      label: "Label",
    },
    none: {
      status: "No status",
      category: "No category",
      project: "No project",
      assignee: "Unassigned",
      priority: "No priority",
      type: "No type",
      epic: "No epic",
      sprint: "No sprint",
      label: "No label",
    },
    notAvailable: "Not available to you: it shows a project or a filter you can't see.",
    failed: "Couldn't load this gadget.",
    retry: "Try again",
    noIssues: "No issues here.",
    issuesTotal: "Issues: {count}",
    issuesMore: "Showing {shown} of {count}",
    openList: "Open the list",
    countsTitle: "{source} by {field}",
    countsSummary: "{count} issues by {field}: {top}.",
    tableTitle: "{source}: {rows} by {columns}",
    tableCaption: "Issues by {rows} and {columns}",
    total: "Total",
    noActiveSprint: "No sprint is running in this project.",
    nothingAssigned: "Nothing assigned to you is unresolved.",
    noActivity: "Nothing has happened here lately.",
    commented: "commented",
    on: "on {key}",
    activityBy: "{actor} {what}",
    gadgetOf: "{name} · {source}",
  },
  /** Personal API tokens (BUILD_SPEC §8.5): a person's key to this workspace's API, shown once, revoked any time. */
  tokens: {
    title: "API tokens",
    theTokens: "the API tokens",
    hint: "A token lets a script or another tool use Steer's API as you, with your access as it is at the time.",
    adminTitle: "API tokens",
    adminBody: "Every API token in the workspace: whose it is, what it may do, and when it was last used. Revoke any.",
    create: "New token",
    createTitle: "New API token",
    createHint: "It acts as you, never with more than you have. You'll see it once.",
    madeTitle: "Your new token",
    madeHint: "Copy it now: Steer keeps only a trace of it, and can't show it again.",
    secretOf: "The token for “{name}”",
    usage: "Send it as Authorization: Bearer <token> to this workspace's address, /api/v1/…",
    mcp: "For an AI agent, add Steer's MCP server at {address} with this token: it acts as you, through MCP.",
    copy: "Copy the token",
    copied: "Token copied",
    done: "Done",
    name: "Name",
    namePlaceholder: "What uses it: “CI pipeline”",
    scope: "What it may do",
    scopes: { read: "Read only", write: "Read and change" },
    projects: "Projects",
    everyProjectChoice: "Every project I can see",
    everyProject: "Every project",
    onlyProjects: "Only {projects}",
    aProject: "a project",
    lasts: "Expires after",
    days: "{count} days",
    ownedBy: "{name}'s",
    expires: "Expires {day}",
    expired: "Expired {day}",
    revoked: "Revoked",
    neverUsed: "Never used",
    lastUsed: "Last used {when}",
    none: "No tokens.",
    revoke: "Revoke",
    revokeNamed: "Revoke “{name}”",
    revokeTitle: "Revoke “{name}”?",
    revokeBody: "Anything using it stops working at once. This can't be undone; make a new token instead.",
    revokedDone: "Revoked “{name}”",
    cancel: "Cancel",
    failed: "Couldn't do that with the token",
  },
  /** Automation (BUILD_SPEC §17): a project's rules — when, if, then — run as themselves with their owner's leave. */
  automation: {
    title: "Automation",
    manage: "change this project's automation",
    body: "Rules that do the routine for you: when something happens, if it's so, then do this. Each runs with its owner's access, never more.",
    settingsBody: "Rules that assign, move, remind and tell, as this project's work happens.",
    open: "Open automation",
    none: "No rules yet. Start from a template, or make one.",
    create: "New rule",
    fromTemplate: "From a template",
    templatesStill: "Rules from a template are included, and can be adjusted once made.",
    galleryTitle: "Start from a template",
    galleryHint: "Each is made for this project and switched on; adjust it after.",
    use: "Use",
    unavailable: "Not here yet: {why}",
    builderTitle: "New rule",
    editTitle: "Edit “{name}”",
    name: "Name",
    when: "When",
    if: "If",
    thenLabel: "Then",
    addCondition: "Add a condition",
    addAction: "Add an action",
    remove: "Remove",
    moveUp: "Move up",
    noConditions: "Always: no conditions.",
    save: "Save",
    cancel: "Cancel",
    saved: "Saved “{name}”",
    made: "Made “{name}”",
    failed: "Couldn't do that with the rule",
    on: "On",
    off: "Off",
    paused: "Paused: {why}",
    switchOn: "Switch on",
    switchOff: "Switch off",
    edit: "Edit",
    history: "Runs",
    hideHistory: "Hide runs",
    delete: "Delete",
    deleteTitle: "Delete “{name}”?",
    deleteBody: "It stops at once. Its runs go with it.",
    deleted: "Deleted “{name}”",
    actionsOf: "{name}: what to do with it",
    ownedBy: "Runs with {name}'s access",
    recent: "In 30 days: runs {runs}, actions {actions}",
    recentFailures: "{count} failed",
    nextRun: "Next {when}",
    tryIt: "Try it",
    tryOn: "On an issue (its key), or leave empty for the last events",
    trying: "What it would do",
    triedNothing: "Nothing would have started it lately.",
    matched: "Matches",
    notMatched: "Doesn't match",
    unknown: "Unknown values, which render empty: {values}",
    runs: "Runs",
    noRuns: "No runs yet.",
    late: "late",
    runOutcomes: { done: "Done", partial: "Partly done", skipped: "Didn't match", failed: "Failed", paused: "Paused" },
    actionOutcomes: { done: "done", skipped: "skipped", refused: "refused" },
    pausedReasons: {
      budget: "it did more than its hourly budget allows",
      owner_gone: "its owner's account is gone",
      owner_not_contributor: "its owner no longer changes work",
      owner_no_access: "its owner can no longer change this project",
      plan_feature: "the workspace's plan no longer includes the rule builder",
      plan_limit: "this month's automation actions are used up; it runs again next month, or with a larger plan",
      plan_unavailable: "Steer couldn't confirm the workspace's plan just then",
    },
    runOn: "Run a rule",
    runOnTitle: "Run a rule on {key}",
    runNone: "No rules run by hand in this project.",
    ran: "“{name}” ran on {key}",
    triggers: {
      "issue.created": "An issue is made",
      "field.changed": "A field changes",
      transitioned: "An issue moves to another status",
      commented: "Someone comments",
      "sprint.started": "A sprint starts",
      "sprint.completed": "A sprint completes",
      "release.released": "A release is released",
      pull_request: "A linked pull request changes on GitHub",
      due: "An issue's due date is near, or past",
      schedule: "On a schedule",
      manual: "Someone runs it from an issue's menu",
    },
    conditions: {
      matches: "The issue matches a query",
      field: "A field's value",
      actor: "Who acted",
      subtasks_resolved: "Every sub-task is resolved",
      links: "It has, or lacks, a link",
      blocked_for: "It has been blocked for days",
    },
    actions: {
      transition: "Move it to a status",
      assign: "Assign it",
      set: "Set or clear a field",
      labels: "Add or remove labels",
      comment: "Comment",
      create: "Make an issue",
      link: "Link it to another issue",
      clone: "Clone it",
      notify: "Tell people",
      webhook: "Send it to a webhook",
    },
    fields: {
      status: "Status",
      assignee: "Assignee",
      priority: "Priority",
      labels: "Labels",
      sprint: "Sprint",
      release: "Release",
      due: "Due date",
      start: "Start date",
      estimate: "Estimate",
      type: "Type",
    },
    trigger: "What starts it",
    field: "Field",
    toValue: "Changes to",
    anyValue: "Anything",
    fromStatus: "From",
    toStatus: "To",
    toCategory: "Or to any status of category",
    anyStatus: "Any status",
    anyCategory: "Any category",
    daysBefore: "Days before it's due",
    overdue: "And the day after it's due",
    pullAction: "When it is",
    pullActions: { opened: "Opened", merged: "Merged", closed: "Closed without merging", reopened: "Reopened" },
    every: "Every",
    everyChoices: { day: "Day", week: "Week", month: "Month" },
    at: "At",
    weekday: "On",
    weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    monthDay: "On day",
    forEach: "For each issue this query finds (optional)",
    query: "SteerQL query",
    valueOf: "Its value",
    valueChoices: { new: "now", old: "before the change" },
    is: "Is",
    isNot: "Isn't",
    empty: "empty",
    equals: "This value",
    who: "Who",
    whoChoices: { not_automation: "Anyone but automation", person: "This person", role: "Someone in this role" },
    roles: { administrator: "Administrator", member: "Member", viewer: "Viewer" },
    person: "Person",
    of: "Of",
    ofChoices: { issue: "the issue itself", parent: "its parent" },
    has: "Has",
    lacks: "Lacks",
    linkKinds: {
      blocks: "blocks another",
      blocked_by: "is blocked by another",
      relates: "relates to another",
      duplicates: "duplicates another",
      duplicated_by: "is duplicated by another",
      clones: "clones another",
      cloned_by: "is cloned by another",
    },
    days: "Days",
    onTarget: "On",
    targets: { issue: "the issue", parent: "its parent" },
    status: "Status",
    assignTo: "To",
    assignChoices: {
      person: "A person",
      reporter: "Its reporter",
      lead: "The project lead",
      actor: "Whoever acted",
      nobody: "Nobody",
    },
    setValue: "To",
    clear: "Clear it",
    relativeDays: "Days from the run (0 is that day)",
    add: "Add",
    removeLabels: "Remove",
    words: "Words",
    templateHint:
      'Values: {{issue.key}}, {{issue.title}}, {{issue.status}}, {{issue.priority}}, {{issue.assignee}}, {{issue.reporter}}, {{issue.due}}, {{issue.url}}, {{actor}}, {{trigger.from}}, {{trigger.to}}, {{now}}; dates take | date:"D MMM YYYY".',
    issueTitle: "Title",
    description: "Description",
    type: "Type",
    defaultType: "The project's default",
    subtask: "As a sub-task of the issue",
    priority: "Priority",
    unchanged: "As usual",
    other: "The other issue's key",
    linkKind: "How",
    tell: "Tell",
    tellChoices: {
      assignee: "Its assignee",
      reporter: "Its reporter",
      watchers: "Its watchers",
      admins: "The project's admins",
      lead: "The project lead",
    },
    message: "Message",
    webhook: "Webhook",
    chooseWebhook: "Choose a webhook",
    choose: "Choose…",
  },
  /** CSV imports (BUILD_SPEC §19.1): a file read, mapped, checked, imported, reported and undone. */
  imports: {
    title: "Import",
    manage: "import into this project",
    theImports: "the project's imports",
    thisImport: "this import",
    settingsBody: "Bring issues in from a CSV file: map its columns, check every row, then import.",
    open: "Import from CSV",
    body: "A CSV file of up to 50 MB and 100,000 rows, saved as UTF-8. Steer reads it, proposes how its columns map, checks every row before writing any, and imports it without telling anyone. Nobody is invited because a file names them.",
    choose: "Choose a CSV file",
    uploading: "Sending {name}…",
    uploadFailed: "Couldn't send {name}",
    failed: "Couldn't do that with the import",
    earlier: "Earlier imports",
    none: "No imports into this project yet.",
    noneEarlier: "None before this one.",
    by: "{name}, {when}",
    size: "{size}",
    states: {
      uploading: "Sending",
      scanning: "Reading",
      mapping: "To map",
      checking: "Checking",
      checked: "Checked",
      importing: "Importing",
      done: "Imported",
      cancelled: "Cancelled",
      failed: "Stopped",
      undone: "Undone",
    },
    reading: "Reading the file: {percent}%",
    checkingRows: "Checking every row: {percent}%",
    importingRows: "Importing: {percent}%, {count} made so far",
    sample: "The file's first rows",
    columnsTitle: "Columns",
    columnsHint: "Which column holds each field. A title is needed; anything not mapped isn't imported.",
    notImported: "Not imported",
    column: "{letter}: {name}",
    needed: "needed",
    fields: {
      key: "Key where it came from",
      id: "Id where it came from, if apart from its key",
      title: "Title",
      description: "Description",
      type: "Type",
      status: "Status",
      priority: "Priority",
      assignee: "Assignee (email or name)",
      reporter: "Reporter (email or name)",
      labels: "Labels",
      start: "Start date",
      due: "Due date",
      estimate: "Estimate",
      parent: "Parent's key or id",
      sprint: "Sprint, by name",
      release: "Release, by name",
    },
    keyHint: "Kept with each issue, so importing the file again updates what it made rather than making it twice.",
    dateFormat: "Dates are written",
    dateFormats: {
      iso: "Year-month-day (2026-12-31)",
      dmy: "Day/month/year (31/12/2026)",
      mdy: "Month/day/year (12/31/2026)",
    },
    labelSeparator: "Labels are separated by",
    separators: { ";": "Semicolons", ",": "Commas", "|": "Bars", " ": "Spaces" },
    valuesTitle: "{field}: each value",
    valuesHint: "What each of the file's values becomes in this project.",
    more: "The file has more values than these: one not mapped here is matched by name, or its row is refused.",
    projectDefault: "The project's default",
    noPriority: "No priority",
    nobody: "Nobody",
    importedPerson: "An imported person (a name, not a login)",
    check: "Check every row",
    remap: "Change the mapping",
    checkedTitle: "Checked: nothing has been written yet",
    doneTitle: "Imported",
    cancelledTitle: "Cancelled: what it made stays",
    undoneTitle: "Undone",
    counts: {
      rows: "Rows read",
      imported: "Made",
      wouldImport: "To be made",
      updated: "Updated",
      wouldUpdate: "To be updated",
      refused: "Refused",
      duplicates: "Duplicates",
    },
    refusalsTitle: "Rows refused",
    refusalsMore: "The first {shown} of {total}.",
    row: "Row",
    reason: "Why",
    download: "Download every refused row",
    run: "Import {issues}",
    issues: ["issue", "issues"] as const,
    cancel: "Cancel import",
    cancelled: "Import cancelled",
    undo: "Undo this import",
    undoTitle: "Undo this import?",
    undoBody:
      "Each issue it made that nobody has changed since goes to the project's trash. Those changed since, and those it only updated, are left as they are.",
    undone: "Removed {removed}.",
    left: "Left as they are:",
    why: {
      changed: "changed since",
      updated: "updated, not made, by the import",
      children: "has issues under it that stay",
    },
    again: "Import another file",
    errors: {
      file_gone: "The file is no longer kept. Send it again.",
      not_utf8: "This file isn't UTF-8 text. Save it as “CSV UTF-8” and send it again.",
      file_empty: "This file is empty.",
      no_rows: "This file has a header but no rows.",
      too_many_rows: "This file has more than 100,000 rows. Split it, and import each part.",
      too_many_columns: "This file has more than 500 columns.",
      row_too_long: "A row of this file is longer than 16 MB.",
      project_gone: "The project is gone.",
      upload_unfinished: "The file never finished arriving. Send it again.",
      capacity:
        "The workspace's data reached the size at which Steer stops imports. What it made is kept, and can be undone.",
      other: "The import stopped. Try it again.",
    },
  },
  /** Imports from Jira Cloud (BUILD_SPEC §19.2): a site's projects found, chosen, mapped, brought, reported, undone. */
  migrations: {
    title: "Import from Jira or Linear",
    theImports: "the workspace's imports",
    thisImport: "this import",
    section: "Bring projects from Jira Cloud, or teams from Linear",
    body: "Bring your work from another tracker into Steer, each project or team as a new project. Nobody is told, invited or made a member. What you lend Steer to read it with is kept sealed while the import runs, and deleted when it's over.",
    sourceLabel: "Import from",
    sources: { jira: "Jira Cloud", linear: "Linear" },
    place: "{source} · {site}",
    jira: {
      body: "Its issues and their hierarchy, comments by their authors, files, labels and components, versions as releases, and sprints.",
      site: "Jira site",
      sitePlaceholder: "https://your-team.atlassian.net",
      email: "Your Atlassian account's email",
      token: "API token",
      tokenHint:
        "Make one in your Atlassian account, under Security › API tokens. Steer uses it for this import alone.",
      connect: "Connect to Jira",
      connecting: "Checking with Jira…",
    },
    linear: {
      body: "Its issues with their sub-issues, comments by their authors, the pictures and files pasted into them, labels, cycles as sprints, and Linear's projects as epics. Archived issues come too.",
      key: "Personal API key",
      keyHint:
        "Make one in Linear under Settings › Security & access › Personal API keys, with read access to the teams to bring. Steer uses it for this import alone.",
      connect: "Connect to Linear",
      connecting: "Checking with Linear…",
    },
    failed: "Couldn't do that with the import",
    states: {
      discovering: "Looking",
      choosing: "To choose",
      reading: "Reading",
      mapping: "To map",
      importing: "Importing",
      done: "Imported",
      cancelled: "Cancelled",
      failed: "Stopped",
      undone: "Undone",
    },
    finding: { jira: "Finding the site's projects…", linear: "Finding the organization's teams…" },
    reading: { jira: "Reading the chosen projects…", linear: "Reading the chosen teams…" },
    chooseTitle: { jira: "Which projects?", linear: "Which teams?" },
    chooseHint: "Each comes as a new project in Steer. Up to 20 in one import.",
    unsupported: "Jira Service Management, which Steer doesn't bring",
    readThem: "Read {projects}",
    projectWords: { jira: ["project", "projects"] as const, linear: ["team", "teams"] as const },
    mapTitle: "How {key} comes across",
    mapHint: {
      jira: "{issues} issues, {sprints} sprints, {versions} versions.",
      linear: "{sprints} cycles, which come as sprints. Its Linear projects come as epics.",
    },
    projectKey: "Key in Steer",
    projectName: "Name in Steer",
    statusesTitle: "Its statuses",
    statusName: "{name}: name in Steer",
    statusCategory: "{name}: category",
    typesTitle: "Its issue types",
    typeOf: "{name} becomes",
    peopleTitle: "People",
    peopleHint: "Someone Steer doesn't know comes as a name on what they did: never invited, never a login.",
    aName: "A name, not a login",
    prioritiesTitle: "Priorities",
    run: "Import",
    importing: "Importing {project}: {step}",
    steps: {
      setup: "setting it up",
      releases: "its versions",
      sprints: "its sprints",
      issues: "its issues",
      files: "its files",
      states: "its sprints' and versions' states",
      links: "the links between issues",
    },
    cancel: "Cancel the import",
    counts: {
      projects: "Projects",
      issues: "Issues",
      comments: "Comments",
      attachments: "Files",
      sprints: "Sprints",
      releases: "Releases",
      links: "Links",
      skipped: "Left out",
    },
    skippedTitle: "Left out, and why",
    notBroughtTitle: "What Steer doesn't bring",
    customFields: "Custom fields, which Steer doesn't have yet: {names}",
    worklogs: "{count} worklogs: Steer has no time tracking yet",
    undo: "Undo this import",
    undoTitle: "Undo this import?",
    undoBody:
      "A project nobody has changed since goes to trash, as a deleted project does, for 30 days. In one someone has, the issues nobody changed go, and the rest are listed.",
    undone: "In trash: {projects}. Issues taken back: {removed}.",
    noProjects: "none",
    left: "Left as they are:",
    why: { changed: "changed since", children: "has issues under it that stay" },
    again: "Import again",
    earlier: "Earlier imports",
    none: "No imports yet.",
    by: "{name}, {when}",
    errors: {
      jira_denied: "Jira stopped accepting the token. Make a new one and import again.",
      jira_missing: "Jira no longer has what the import was reading.",
      jira_refused: "Jira refused what the import asked of it.",
      linear_denied: "Linear stopped accepting the API key. Make a new one and import again.",
      linear_refused: "Linear refused what the import asked of it.",
      no_import_key: "Importing from other trackers isn't set up for Steer yet.",
      starter_gone: "The Administrator who began the import is no longer one.",
      credentials_unreadable: "Steer couldn't read what it was lent to read with.",
      no_credentials: "The import has nothing to read with.",
      capacity:
        "The workspace's data reached the size at which Steer stops imports. What it brought is kept, and can be undone.",
      other: "The import stopped. Try it again.",
    },
  },
  /** Slack (BUILD_SPEC §18.4): Steer's app in a team, people's own accounts, and a project's channels. */
  slack: {
    title: "Slack",
    status: "Slack's connection",
    section: "Tell channels what happens, and show links to Steer",
    body: "Install Steer's app in your Slack team: each project's admins choose channels to tell of its issues, comments, sprints and releases, and a link to Steer shared in Slack shows its title to whoever connected their Slack account and may see the issue, its key alone to everyone else.",
    notConfigured: "Steer's Slack app isn't set up for Steer yet.",
    install: "Add to Slack",
    installing: "Going to Slack…",
    teams: "Installed in",
    none: "Not installed in a Slack team yet.",
    by: "Installed by {name}, {when}",
    revoked: "Slack says the app is gone: install it again",
    remove: "Remove",
    removeTitle: "Remove Steer's app from {team}?",
    removeBody:
      "Steer revokes its token there and stops telling its channels anything; people's connected accounts in it are forgotten.",
    outcomes: {
      installed: "Steer's app is in your Slack team.",
      connected: "Your Slack account is connected.",
      denied: "Slack wasn't allowed to finish. Nothing changed.",
      not_admin: "Only Steer Administrators can install Steer's app in Slack.",
      failed: "Slack didn't finish. Try again.",
      wrong_team: "You signed in to another Slack team than the one asked for, so nothing was connected.",
      expired: "That took too long, or was already used. Try again.",
      unavailable: "Steer's Slack app isn't set up for Steer yet.",
    },
    me: "Your Slack account",
    meBody:
      "Connect your account in each Slack team, so a link to Steer you share there shows its title, as you may see it.",
    connect: "Connect",
    disconnect: "Disconnect",
    connectedIn: "Connected in {team}",
    notConnectedIn: "Not connected in {team}",
    channels: "Slack channels",
    channelsBody: "Channels in your Slack teams told what happens in this project, as you see it.",
    noTeams: "Install Steer's app in a Slack team first, from Settings › Slack.",
    noChannels: "No channels yet.",
    team: "Slack team",
    channel: "Channel",
    chooseChannel: "Choose a channel",
    privateChannel: "{name} (private)",
    add: "Add channel",
    tell: "Tell it of",
    events: {
      "issue.created": "Issues made",
      "issue.transitioned": "Issues moving between statuses",
      "comment.created": "Comments",
      "sprint.started": "Sprints started",
      "sprint.completed": "Sprints completed",
      "release.released": "Releases released",
    },
    saved: "Saved",
    removeChannel: "Stop telling #{name}",
    errors: {
      not_installed: "Steer's app isn't in this Slack team any more.",
      channel_not_found: "Slack can't find this channel.",
      not_in_channel: "Invite Steer's app to this private channel.",
      is_archived: "This channel is archived.",
      channel_is_archived: "This channel is archived.",
      other: "Slack refused the last message: {error}",
    },
  },
  /** GitHub (BUILD_SPEC §18.3): connecting Steer's app, and an issue's development. */
  github: {
    title: "GitHub",
    status: "GitHub's connection",
    section: "Link branches, commits and pull requests",
    body: "Name an issue's key, such as APP-142, in a branch, a commit message or a pull request on GitHub, and it shows on the issue, with its checks. A merged pull request can move its issue on, if a project's rule says so.",
    notConfigured: "Steer's GitHub App isn't set up for Steer yet.",
    connect: "Connect GitHub",
    connecting: "Going to GitHub…",
    connectHint:
      "GitHub asks which organization or account, and which of its repositories, to give Steer. Steer reads its events alone: it never writes to GitHub.",
    installations: "Connected",
    none: "Nothing connected yet.",
    organization: "Organization",
    user: "Personal account",
    allRepositories: "All its repositories",
    chosenRepositories: "Chosen repositories",
    by: "Connected by {name}, {when}",
    suspended: "Suspended on GitHub",
    manage: "Manage on GitHub",
    disconnect: "Disconnect",
    disconnectTitle: "Disconnect {account}?",
    disconnectBody:
      "Steer stops taking its events; what it linked stays on the issues. To take Steer's app off GitHub too, uninstall it there.",
    repositories: "Repositories heard from",
    outcomes: {
      connected: "GitHub is connected.",
      requested: "An owner of the organization has to approve Steer's app on GitHub. Connect it again once they have.",
      not_yours: "GitHub didn't show you that installation, so it isn't connected.",
      not_admin: "Only Steer Administrators can connect GitHub.",
      failed: "GitHub didn't finish connecting. Try again.",
      expired: "That took too long, or was already used. Connect GitHub again.",
      unavailable: "Steer's GitHub App isn't set up for Steer yet.",
    },
    development: "Development",
    copyBranch: "Copy branch name",
    copied: "Branch name copied",
    branches: "Branches",
    commits: "Commits",
    pullRequests: "Pull requests",
    moreCommits: "{count} more",
    deleted: "deleted",
    empty: "Name {key} in a branch, a commit message or a pull request on GitHub to link it here.",
    states: { open: "Open", draft: "Draft", merged: "Merged", closed: "Closed" },
    checks: { pending: "Checks running", success: "Checks passed", failure: "Checks failed", neutral: "Checks done" },
  },
  /** Exports (BUILD_SPEC §19.6): a project's, or the workspace's, as a ZIP of what the person may see. */
  plan: {
    readOnly: {
      title: "Read-only.",
      trial_ended: "This workspace's trial has ended.",
      canceled: "This workspace's subscription has ended.",
      no_subscription: "This workspace has no subscription to Steer.",
      body: "Everything can still be read and exported, and nothing has been deleted. Ask Optymyse to choose a plan.",
      export: "Export",
    },
    trial: {
      title: "Trial.",
      body: "{days} left of this workspace's trial of {plan}.",
      lastDay: "This is the last day of this workspace's trial of {plan}.",
    },
    pastDue: {
      title: "A payment is overdue.",
      body: "Unless it's paid by {date}, this workspace becomes read-only. Nothing is deleted.",
      bodyUndated: "Unless it's paid, this workspace becomes read-only. Nothing is deleted.",
    },
    ending: {
      title: "The subscription is ending.",
      body: "This workspace becomes read-only on {date}. Nothing is deleted, and it can still be exported.",
    },
    unavailable: {
      title: "Steer can't confirm this workspace's plan just now.",
      body: "Work goes on as usual; what the plan decides waits until Steer can ask again.",
    },
    days: (n: number) => (n === 1 ? "1 day" : `${n} days`),
    notIncluded: {
      title: "This workspace's plan doesn't include {feature}.",
      body: "Ask Optymyse about a plan that includes it. Nothing has been hidden or deleted.",
    },
    full: {
      title: "This workspace can't import more.",
      body: "Its own data, which is its issues, their history and comments, and search, has reached {size}. At that size Steer stops imports from files and from other trackers. Everyday work, erasure and export carry on as usual, and Optymyse has been told.",
      usage: "Usage",
    },
  },
  audit: {
    title: "Audit trail",
    noneInArea: "Nothing in the trail is of this kind",
    section: "Who changed what, and when",
    body: "Every change of the workspace's settings and access, newest first: who made it, from where, and what it changed. It's kept 13 months.",
    area: "Show",
    areas: {
      all: "Everything",
      people: "People and invitations",
      access: "API tokens",
      projects: "Projects, boards, releases and labels",
      automation: "Automation",
      integrations: "Webhooks, Slack and GitHub",
      data: "Imports and exports",
      plan: "The plan",
    },
    when: "When",
    who: "Who",
    what: "What",
    from: "From",
    empty: "Nothing has been changed here yet.",
    more: "Show older changes",
    loading: "Loading…",
    failed: "Couldn't load the audit trail",
    retry: "Try again",
    changed: "changed",
    exportTitle: "Export",
    exportBody:
      "Whole days, oldest first, as CSV: {days} days back at most on this workspace's plan. Exporting is recorded here too.",
    exportNone:
      "This workspace's plan doesn't include exporting the audit trail. Ask Optymyse about a plan that includes it.",
    exportUnknown: "Steer can't confirm this workspace's plan just now, so exporting waits until it can.",
    firstDay: "First day",
    lastDay: "Last day",
    download: "Download CSV",
    actions: {
      "project.created": "made the project",
      "project.updated": "changed the project",
      "project.deleted": "deleted the project",
      "project.restored": "restored the project",
      "project.purged": "deleted the project for good",
      "project.visibility_changed": "changed who sees the project",
      "project.joined": "joined the project",
      "project.member_removed": "took someone off the project",
      "board.configured": "set up the board of",
      "workflow.status_added": "added a status to the workflow of",
      "workflow.status_updated": "changed a status in the workflow of",
      "workflow.status_removed": "removed a status from the workflow of",
      "workflow.reordered": "reordered the workflow of",
      "sprint.started": "started",
      "sprint.completed": "completed",
      "issue.deleted": "deleted an issue",
      "issue.purged": "deleted an issue for good",
      "export.downloaded": "downloaded an export",
      "release.created": "made the release",
      "release.released": "released",
      "release.deleted": "deleted the release",
      "label.updated": "changed the label",
      "label.deleted": "deleted the label",
      "label.merged": "merged labels into",
      "issue.moved": "moved an issue to another project:",
      "automation.rule_created": "made the rule",
      "automation.rule_updated": "changed the rule",
      "automation.rule_deleted": "deleted a rule",
      "webhook.created": "added the webhook",
      "webhook.updated": "changed the webhook",
      "webhook.deleted": "deleted the webhook",
      "webhook.disabled": "switched off the webhook",
      "webhook.secret_rotated": "gave the webhook a new secret",
      "slack.installed": "installed Slack in",
      "slack.removed": "took Slack out of",
      "slack.channel_added": "told a Slack channel of a project:",
      "slack.channel_removed": "stopped telling a Slack channel:",
      "github.connected": "connected GitHub:",
      "github.disconnected": "disconnected GitHub:",
      "import.started": "started importing",
      "import.run": "imported",
      "import.cancelled": "cancelled importing",
      "import.undone": "undid the import of",
      "migration.started": "started bringing in from",
      "migration.run": "brought in from",
      "migration.done": "finished bringing in from",
      "migration.cancelled": "cancelled bringing in from",
      "migration.undone": "undid what was brought in from",
      "export.requested": "asked for an export",
      "export.ready": "had an export made:",
      "plan.changed": "changed the plan",
      "audit.exported": "exported the audit trail",
      "person.role_changed": "changed the role of",
      "person.removed": "took Steer away from",
      "person.organisation_role_changed": "changed the organisation role of",
      "person.removed_from_organisation": "removed from the organisation",
      "person.roles_given": "gave an invitation's roles to",
      "person.roles_removed": "took Steer's roles from",
      "invitation.sent": "sent an invitation",
      "invitation.resent": "sent an invitation again",
      "invitation.cancelled": "cancelled an invitation",
      "token.created": "made an API token",
      "token.revoked": "revoked an API token",
    } as Readonly<Record<string, string>>,
    /**
     * An accepted invitation whose roles weren't given (`person.roles_given` with `access_since`, SuiteId 0.12.0): what
     * became of the access accepting it gave, after they accepted and before Steer came to give its roles, so it gave
     * none of them, rather than that it made them what it invited them as.
     */
    rolesWithheld: {
      revoked:
        "gave none of an invitation's roles to {name}, as their access to Steer was taken away after they accepted it",
      suspended:
        "gave none of an invitation's roles to {name}, as their access to Steer was suspended after they accepted it",
      gone: "gave none of an invitation's roles to {name}, as their access to Steer ended with their place in the organisation after they accepted it",
    },
    /** Whom an entry is about, where the workspace has no name for them: someone who never opened Steer, say. */
    someone: "someone",
  },
  usage: {
    title: "Usage",
    section: "What the workspace uses, against its plan",
    body: "What the workspace uses now, against what its plan allows. Months are UTC calendar months, and nothing carries over.",
    unenforced:
      "Steer isn't enforcing a plan for this workspace yet, so nothing here is limited. The figures are what it uses.",
    unavailable: "Steer can't confirm this workspace's plan just now. These figures are against the plan it last read.",
    plan: "Plan",
    trialEnds: "Trial ends {date}",
    renews: "Renews {date}",
    ends: "Ends {date}",
    of: "{used} of {quota}",
    noLimit: "{used}, no limit",
    none: "{used}, none included",
    warning: { 70: "70% used", 90: "90% used", 100: "Limit reached" },
    contributors: "Contributors",
    contributorsHint: "Administrators and Members. Viewers are free.",
    people: "Change roles in People",
    viewers: "Viewers",
    files: "Files kept",
    filesHint: "Every file, including those in trash until they're deleted for good. Exports don't count.",
    automation: "Automation actions this month",
    automationHint:
      "Each issue changed or made, comment added, or notification or webhook sent by a rule. Starts again on {date}.",
    rules: "Rules switched on",
    webhooks: "Webhook endpoints",
    data: "Workspace data",
    dataHint:
      "Issues, their history and comments, and search; files aren't counted. Imports stop at {stop}, and everyday work carries on.",
    dataNear: "Nearing where imports stop",
    dataFull: "Imports stopped",
    limitsTitle: "Other limits",
    largestFile: "Largest file",
    apiPerMinute: "API requests a minute, all tokens together",
    auditExport: "Audit trail export",
    auditExportDays: "{days} days back",
    unlimited: "No limit",
    notIncluded: "Not included",
    missing: "The plan doesn't name {limits}, so Steer holds {them} at none until it does.",
    daysTitle: "Each day this month",
    daysNone: "No day of this month is over yet.",
    day: "Day",
    failed: "Couldn't load the workspace's usage",
    retry: "Try again",
  },
  exports: {
    title: "Exports",
    yourExports: "your exports",
    section: "Your projects, or the workspace, to download",
    body: "A ZIP of JSON and CSV with the original files: what you can see, and never a token or a secret. It's made in the background, downloadable for 7 days, each link for 24 hours.",
    projectTitle: "Export a project",
    project: "Project",
    chooseProject: "Choose a project",
    exportProject: "Export the project",
    workspaceTitle: "Export the whole workspace",
    workspaceBody: "Every project you can see, as one archive. Private projects you haven't joined aren't in it.",
    exportWorkspace: "Export the workspace",
    asked: "Your export is being made. It appears below when it's ready.",
    failed: "Couldn't ask for that export",
    yours: "Your exports",
    none: "No exports yet.",
    whole: "The whole workspace",
    states: { building: "Being made", ready: "Ready", failed: "Stopped", expired: "No longer kept" },
    counts: "{issues} issues, {comments} comments, {attachments} files",
    taken: "Taken {when}",
    expires: "Kept until {when}",
    download: "Download",
    downloadOf: "Download {name}",
    settingsBody: "Download the project's issues, comments, history and files as a ZIP.",
    open: "Open exports",
  },
  /** Webhooks (BUILD_SPEC §18.2): addresses told of the workspace's events, signed, tried again, switched off. */
  webhooks: {
    title: "Webhooks",
    theEndpoints: "the webhook endpoints",
    theDeliveries: "the deliveries",
    whoAdds: "Steer's Administrators and a project's admins",
    addThem: "add webhooks",
    body: "Tell another service when something happens here: each event is sent as it is, signed, and sent again if it doesn't arrive.",
    none: "No webhooks yet.",
    add: "Add an endpoint",
    addTitle: "Add an endpoint",
    editTitle: "Edit “{name}”",
    addHint: "What it's sent is read as you see it when it's sent: what you can't see isn't sent.",
    name: "Name",
    url: "Address",
    urlHint: "A public https:// address. Local and private addresses aren't sent to.",
    events: "What it's told of",
    project: "Project",
    everyProject: "Every project",
    chooseProject: "Choose a project",
    aProject: "A project",
    save: "Save",
    saved: "Saved",
    cancel: "Cancel",
    secretTitle: "The endpoint's signing secret",
    secretHint: "Copy it now: it isn't shown again. Rotate it to get a new one.",
    secretOf: "The secret for “{name}”",
    verify:
      'Each delivery carries Steer-Signature: t=<seconds>,v1=<HMAC-SHA256 of "t.body" with this secret>. After a rotation, the old secret signs too, for a day.',
    copy: "Copy the secret",
    copied: "Secret copied",
    done: "Done",
    on: "On",
    off: "Off",
    offFailing: "Off: it kept failing",
    ownedBy: "By {name}'s leave",
    failures: "Failed {count} times in a row",
    actions: "What to do",
    actionsOf: "{name}: what to do with it",
    ping: "Send a test",
    pinged: "Test sent",
    edit: "Edit",
    rotate: "Rotate the secret",
    switchOff: "Switch off",
    switchOn: "Switch on again",
    delete: "Delete",
    deleteTitle: "Delete “{name}”?",
    deleteBody: "It's told nothing more, and its deliveries go with it.",
    deleted: "Deleted “{name}”",
    showDeliveries: "Show the deliveries of {name}",
    hideDeliveries: "Hide the deliveries of {name}",
    deliveriesOf: "The latest deliveries of {name}",
    noDeliveries: "Nothing sent yet.",
    event: "Event",
    state: "How it went",
    answer: "Answer",
    states: { pending: "On its way", delivered: "Delivered", failed: "Failed", skipped: "Not sent" },
    attempts: "{count} tries",
    errors: {
      timeout: "No answer in 10 seconds",
      unreachable: "Couldn't reach it",
      address_not_public: "Not a public address",
      too_many_redirects: "Too many redirects",
      redirect_invalid: "A redirect to nowhere",
      not_visible: "Not yours to see now",
      owner_gone: "Its owner is gone",
      endpoint_off: "Switched off",
    },
    sendAgain: "Send again",
    resent: "Sent again",
    failed: "Couldn't do that with the webhook",
  },
  /** Filters (BUILD_SPEC §12.9, §13.1): a query of the issues across projects, built or typed, saved and shared. */
  filters: {
    area: "Filters",
    thisFilter: "this filter",
    theResults: "the issues",
    allIssues: "All issues",
    allIssuesBody: "Every issue you can see, as the query says.",
    starred: "Starred",
    yours: "Your filters",
    shared: "Shared with you",
    query: "Query",
    builder: "Conditions",
    asText: "SteerQL",
    builderLabel: "The query's conditions",
    textLabel: "The query in SteerQL",
    tooComplex: "This query uses OR or NOT, which the conditions can't show: edit it as SteerQL.",
    addCondition: "Add a condition",
    removeCondition: "Remove the condition on {field}",
    field: "Field",
    operator: "How it compares",
    value: "Value",
    valueOf: "Values of {field}",
    addValue: "Add a value",
    removeValue: "Remove {value}",
    datePlaceholder: "2026-10-01, -7d or startOfWeek()",
    textPlaceholder: "Words to find",
    numberPlaceholder: "A number",
    run: "Run the query",
    checking: "Checking…",
    useSuggestion: "Use “{suggestion}”",
    atPosition: "At character {at}",
    fixToSee: "Fix the query to see its issues.",
    tooBroad: "That query reads more issues than Steer reads for one page. Narrow it: by project, status or dates.",
    empty: "No issues match this query.",
    total: "{count}",
    totalMore: "More than {count}",
    save: "Save",
    saveAs: "Save as…",
    saved: "Saved “{name}”",
    star: "Star “{name}”",
    unstar: "Unstar “{name}”",
    share: "Sharing…",
    delete: "Delete",
    deleteTitle: "Delete “{name}”?",
    deleteBody: "Anyone it's shared with will no longer have it. The issues it finds stay as they are.",
    deleted: "Deleted “{name}”",
    edited: "Edited, not saved",
    saveTitle: "Save the filter",
    shareTitle: "Share “{name}”",
    name: "Name",
    sharing: "Who sees it",
    sharingPrivate: "Only you",
    sharingProject: "A project's people",
    sharingWorkspace: "Everyone in the workspace",
    sharingHint: "Sharing a filter shares the question, never the answers: each person sees only the issues they may.",
    project: "Project",
    chooseProject: "Choose a project",
    owner: "{name}'s",
    restrictedHint: "Restricted: part of this query names what you can't see, and finds nothing there for you.",
    cancel: "Cancel",
    saveFailed: "Couldn't save the filter",
    ops: {
      "=": "is",
      "!=": "is not",
      in: "is one of",
      "not in": "is none of",
      ">": "after",
      ">=": "on or after",
      "<": "before",
      "<=": "on or before",
      "~": "contains",
      "!~": "doesn't contain",
      empty: "is empty",
      "not empty": "is not empty",
    },
    orderOps: { ">": "more than", ">=": "at least", "<": "less than", "<=": "at most" },
    priorityOps: { ">": "higher than", ">=": "at least", "<": "lower than", "<=": "at most" },
    fields: {
      project: "Project",
      key: "Key",
      type: "Type",
      status: "Status",
      category: "Status category",
      priority: "Priority",
      assignee: "Assignee",
      reporter: "Reporter",
      label: "Label",
      sprint: "Sprint",
      parent: "Parent",
      epic: "Epic",
      created: "Created",
      updated: "Updated",
      started: "Started",
      resolved: "Resolved",
      start: "Start date",
      due: "Due date",
      estimate: "Estimate",
      customer: "Customer",
      watcher: "Watcher",
      blocked: "Blocked",
      text: "Any text",
      title: "Title",
      description: "Description",
      comment: "Comments",
    },
  },
  /** The command bar (BUILD_SPEC §4.3): go anywhere, find any issue, and act on the issue in view or the selection. */
  commands: {
    label: "Command bar",
    placeholder: "Go to, find an issue, or run a command…",
    empty: "No issues or commands match “{query}”",
    issues: "Issues",
    recent: "Recently viewed",
    goTo: "Go to",
    people: "People",
    assignedTo: "Assigned to {name}",
    create: "Create",
    keyboard: "Keyboard",
    selection: "{count} selected",
    assignToMe: "Assign to me",
    assignTo: "Assign to…",
    assignPage: "Assign {key}",
    findPerson: "Find a person…",
    status: "Change status…",
    statusPage: "Status of {key}",
    findStatus: "Find a status…",
    priority: "Set priority…",
    priorityPage: "Priority of {key}",
    addLabel: "Add a label…",
    addLabelPage: "Add a label to {key}",
    removeLabel: "Remove a label…",
    removeLabelPage: "Remove a label from {key}",
    findLabel: "Find a label…",
    sprint: "Move to a sprint…",
    sprintPage: "Sprint of {key}",
    estimate: "Set the estimate…",
    estimatePage: "Estimate of {key}",
    points: ["point", "points"],
    hours: ["hour", "hours"],
    clearEstimate: "Clear the estimate",
    typeEstimate: "Type an estimate…",
    due: "Set the due date…",
    duePage: "Due date of {key}",
    dueToday: "Today",
    dueTomorrow: "Tomorrow",
    dueInWeek: "In a week",
    clearDue: "Clear the due date",
    pickDue: "Pick a date…",
    current: "Now",
    selectionStatus: "Change the status of {count}…",
    selectionStatusPage: "Status of {count}",
    selectionAssign: "Assign {count} to…",
    selectionAssignPage: "Assign {count}",
    selectionAssignMe: "Assign {count} to me",
    selectionPriority: "Set the priority of {count}…",
    selectionPriorityPage: "Priority of {count}",
    selectionLabel: "Add a label to {count}…",
    selectionLabelPage: "Add a label to {count}",
    selectionSprint: "Move {count} to a sprint…",
    selectionSprintPage: "Sprint of {count}",
    clearSelection: "Clear the selection",
    board: "{name} board",
    backlog: "{name} backlog",
    list: "{name} list",
    settings: "Settings",
    createIssue: "Create an issue",
    createIn: "Create an issue in…",
    findProject: "Find a project…",
    shortcuts: "Keyboard shortcuts",
    shortcutsOff: "Turn single-key shortcuts off",
    shortcutsOn: "Turn single-key shortcuts on",
    turnedOff: "Single-key shortcuts are off. ⌘K or Ctrl+K still opens the command bar.",
    turnedOn: "Single-key shortcuts are on.",
    assignedToYou: "{key} is assigned to you",
    alreadyYours: "{key} is already assigned to you",
  },
  /** The sheet of every shortcut, which `?` opens (BUILD_SPEC §4.3). */
  shortcuts: {
    title: "Keyboard shortcuts",
    description: "Every key, by where it works. None of them act while you're typing in a field.",
    switch: "Single-key shortcuts",
    switchHint:
      "Letters such as J, K and S act on issues. Turn them off if they get in your way; ⌘K or Ctrl+K and the board's arrows and Space still work. Kept with you, in every browser.",
    followedBy: "then",
    anywhere: "Anywhere",
    onIssues: "On an issue or a selection",
    onBoard: "On a board",
    commandBar: "Open the command bar",
    create: "Create an issue",
    search: "Search",
    goInbox: "Go to the Inbox",
    goMyWork: "Go to My work",
    goProjects: "Go to Projects",
    goBoard: "Go to the project's board",
    help: "Show these shortcuts",
    next: "Next issue",
    previous: "Previous issue",
    open: "Open the focused issue",
    select: "Select or unselect it, in a list or the backlog",
    assign: "Assign it",
    assignMe: "Assign it to me",
    status: "Change its status",
    priority: "Change its priority",
    labels: "Add a label",
    estimate: "Set its estimate",
    due: "Set its due date",
    close: "Close or cancel",
    saveComment: "Save a comment",
    arrows: "Move between cards and columns",
    pickUp: "Pick a card up, and drop it",
    putBack: "Put a picked-up card back",
    moveTo: "Choose where a card goes",
  },
} as const;
